import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Col, Container, Row, Spinner, Table } from 'react-bootstrap';
import { useKeycloak } from '@react-keycloak/web';
import moment from 'moment';
import {
  ApiError,
  getMe,
  MeResponse,
} from './ApiClient';
import { GESCHLECHT_LABEL } from '../generateDiff';
import { StatesAlpha2List } from '../statesAlpha2List';

const DATE_FORMAT = 'DD.MM.YYYY';

const STATUS_LABEL: Record<number, string> = {
  0: 'Unbekannt',
  1: 'Angehalten',
  2: 'Zuverlässig',
  3: 'Unzuverlässig',
};

const STATUS_VARIANT: Record<number, string> = {
  0: 'secondary',
  1: 'warning',
  2: 'success',
  3: 'danger',
};

const BESCHAEFTIGUNG_ART_LABEL: Record<number, string> = {
  0: 'Beschäftigung',
  1: 'Ausbildung',
  2: 'Arbeitslos',
  3: 'Selbständig',
  4: 'nicht zutreffend (Lücke)',
};

// USP-REST dokinfo.art (verifiziert am Echtsystem 2026-07-06):
// 0 = Strafregisterbescheinigung, 1 = Reisepass, 2 = Personalausweis.
const DOK_ART_LABEL: Record<number, string> = {
  0: 'Strafregisterbescheinigung',
  1: 'Reisepass',
  2: 'Personalausweis',
};

/** Ausweisdokumente (Reisepass/Personalausweis) sind in AT/EU für Erwachsene
 * i.d.R. 10 Jahre gültig; eine Strafregisterbescheinigung (art 3) wird von
 * der Behörde nur akzeptiert, wenn sie bei Einreichung höchstens 6 Monate
 * alt ist. USP speichert nur das Ausstellungsdatum, daher ist der Ablauf
 * eine SCHÄTZUNG (so gekennzeichnet). */
function estimatedExpiry(doc: { art: number; ausstellung: string }): moment.Moment | null {
  const m = moment(doc.ausstellung);
  if (!m.isValid()) return null;
  if (doc.art === 1 || doc.art === 2) return m.add(10, 'years');
  if (doc.art === 0) return m.add(6, 'months');
  return null;
}

/** Maskiert eine Dokumentnummer für die Anzeige: nur die letzten 3 Zeichen. */
function maskNummer(nummer?: string | null): string {
  if (!nummer) return '–';
  return nummer.length <= 3 ? nummer : '•••' + nummer.slice(-3);
}

/** Bekannte Offenliste-Keys -> verständliche To-do-Hinweise.
 * Unbekannte Keys werden mit ihrem Rohnamen angezeigt, damit neue
 * Bedingungen der Behörde nicht stillschweigend verschluckt werden. */
const OFFEN_LABEL: Record<string, string> = {
  p_daten: 'Pflichtfeld nicht befüllt',
  id_dok: 'Identitätsdokument fehlt',
  ws_luecke: 'Wohnsitzlücke in den letzten 5 Jahren',
  ws_aktuell: 'Kein aktueller Wohnsitz hinterlegt',
  bv_luecke: 'Beschäftigungslücke (>28 Tage) in den letzten 5 Jahren',
  bv_aktuell: 'Keine aktuelle Beschäftigung/Ausbildung hinterlegt',
  sr_auszug: 'Strafregisterbescheinigung nötig (Auslands-Wohnsitz)',
};

function formatDate(value?: string | null): string {
  if (!value) return '–';
  const m = moment(value);
  return m.isValid() ? m.format(DATE_FORMAT) : value;
}

function countryLabel(code?: string | null): string {
  if (!code) return '–';
  return StatesAlpha2List.ALPHA2CODES[code] || code;
}

function MeinZuep() {
  const { keycloak } = useKeycloak();
  const [data, setData] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getMe(keycloak.token);
      setData(result);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setError('Für dein Konto ist noch kein ZÜP-Datensatz verknüpft. Bitte wende dich an die Vereinsverwaltung.');
      } else {
        setError('Deine ZÜP-Daten konnten nicht geladen werden: ' + (err as Error).message);
      }
    } finally {
      setLoading(false);
    }
  }, [keycloak.token]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Container fluid='md' className='py-4 text-center'>
        <Spinner animation='border' role='status' />
        <p className='mt-3'>Lade deine ZÜP-Daten…</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid='md' className='py-4'>
        <Alert variant='danger'>{error}</Alert>
        <Button variant='outline-secondary' onClick={load}>Erneut versuchen</Button>
      </Container>
    );
  }

  if (!data) {
    return (
      <Container fluid='md' className='py-4'>
        <Alert variant='warning'>Keine Daten vorhanden.</Alert>
      </Container>
    );
  }

  const { person, adressen, beschaeftigung, dokinfo, status, offen } = data;
  const offenEntries = Object.entries(offen || {}).filter(
    ([key, value]) => key !== 'p_id' && value !== false && value !== null && value !== undefined,
  );
  // Behörden-Regel (Bartek, 31.08.2026): Frischer Strafregisterauszug
  // (<6 Monate) nur bei AKTUELLEM Auslands-Wohnsitz; frühere Auslands-
  // Wohnsitze brauchen nur die EINMALIGE Vorlage.
  const auslandsWohnsitz = adressen.some((a) => !a.bis && a.land && a.land !== 'AT');

  return (
    <Container fluid='md' className='py-4'>
      <h2 className='mb-3'>Mein ZÜP-Datensatz</h2>
      <p className='text-muted'>
        Nur-lesende Übersicht deines Zuverlässigkeitsüberprüfungs-Datensatzes (P-ID {data.p_id}).
        Änderungen bitte über die Vereinsverwaltung veranlassen.
      </p>

      <section className='form-section mb-4'>
        <h5 className='mb-3'>ZÜP-Status</h5>
        <Row className='py-2'>
          <Col md={6} xs={12} className='mb-2'>
            <div className='text-muted small'>Neuantrag (N)</div>
            <div>
              <Badge bg={status.n_status !== undefined && status.n_status !== null ? STATUS_VARIANT[status.n_status] ?? 'secondary' : 'secondary'}>
                {status.n_status !== undefined && status.n_status !== null ? STATUS_LABEL[status.n_status] ?? `Code ${status.n_status}` : 'Kein Neuantrag'}
              </Badge>
              {status.n_frist && <span className='ms-2 text-muted'>Frist: {formatDate(status.n_frist)}</span>}
            </div>
          </Col>
          <Col md={6} xs={12} className='mb-2'>
            <div className='text-muted small'>Erweiterung (E)</div>
            <div>
              <Badge bg={status.e_status !== undefined && status.e_status !== null ? STATUS_VARIANT[status.e_status] ?? 'secondary' : 'secondary'}>
                {status.e_status !== undefined && status.e_status !== null ? STATUS_LABEL[status.e_status] ?? `Code ${status.e_status}` : 'Keine Erweiterung'}
              </Badge>
              {status.e_frist && <span className='ms-2 text-muted'>Frist: {formatDate(status.e_frist)}</span>}
            </div>
          </Col>
        </Row>
        {auslandsWohnsitz && (
          <Alert variant='warning' className='mb-0 mt-2'>
            <strong>Aktueller Auslands-Wohnsitz:</strong> Für jede ZÜP-Verlängerung brauchst du eine
            <strong> Strafregisterbescheinigung</strong> aus deinem Wohnsitzland, die bei Einreichung
            <strong> höchstens 6 Monate alt</strong> ist. Besorge sie am besten kurz bevor das
            Verlängerungsfenster beginnt (ca. 2 Monate vor Ablauf) — der abonnierte FZT-Kalender erinnert dich daran.
          </Alert>
        )}
      </section>

      <section className='form-section mb-4'>
        <h5 className='mb-2'>📅 FZT-Kalender</h5>
        <p className='text-muted mb-2' style={{ fontSize: '0.9rem' }}>
          Dein persönlicher Kalender zum Abonnieren — mit deinen Flugzeug-Reservierungen und
          allen Fälligkeiten: ZÜP-Ablauf, Flughafenausweis und bei Auslands-Wohnsitz die
          Erinnerung, rechtzeitig den Strafregisterauszug zu besorgen. Aktualisiert sich
          automatisch in deiner Kalender-App.
        </p>
        <Button
          variant='outline-primary'
          size='sm'
          href='https://ical.flugsportzentrum.at/'
          target='_blank'
          rel='noreferrer'
        >
          FZT-Kalender abonnieren
        </Button>
      </section>

      <section className='form-section mb-4'>
        <h5 className='mb-3'>Offene Punkte</h5>
        {offenEntries.length === 0 ? (
          <Alert variant='success' className='mb-0'>Alle Angaben vollständig — nichts offen.</Alert>
        ) : (
          <Alert variant='warning' className='mb-0'>
            <ul className='mb-0'>
              {offenEntries.map(([key]) => (
                <li key={key}>{OFFEN_LABEL[key] || key}</li>
              ))}
            </ul>
          </Alert>
        )}
      </section>

      {data.ausweis_info && (
        <section className='form-section mb-4'>
          <h5 className='mb-3'>Flughafenausweis</h5>
          <Table borderless size='sm' responsive>
            <tbody>
              <tr>
                <td className='text-muted'>Gültig bis</td>
                <td>
                  <strong>{formatDate(data.ausweis_info.gueltig_bis)}</strong>
                </td>
              </tr>
              {data.ausweis_info.status && (
                <tr><td className='text-muted'>Status</td><td>{data.ausweis_info.status}</td></tr>
              )}
              {data.ausweis_info.schulung && (
                <tr><td className='text-muted'>Sicherheitsschulung</td><td>{formatDate(data.ausweis_info.schulung)}</td></tr>
              )}
            </tbody>
          </Table>
          <Badge bg='light' text='dark' className='border'>
            Daten der Ausweisstelle — Stand {formatDate(data.ausweis_info.stand)}, nicht tagesaktuell
          </Badge>
        </section>
      )}

      <section className='form-section mb-4'>
        <h5 className='mb-3'>Persönliche Daten</h5>
        <Table borderless size='sm' responsive>
          <tbody>
            <tr><td className='text-muted'>Name</td><td>{[person.vorname, person.famname].filter(Boolean).join(' ')}</td></tr>
            {person.gebname && <tr><td className='text-muted'>Geburtsname</td><td>{person.gebname}</td></tr>}
            <tr><td className='text-muted'>Geschlecht</td><td>{GESCHLECHT_LABEL[person.geschlecht] ?? person.geschlecht}</td></tr>
            <tr><td className='text-muted'>Geburtsdatum</td><td>{formatDate(person.gebdatum)}</td></tr>
            <tr><td className='text-muted'>Geburtsort</td><td>{[person.geb_ort, person.geb_ort_plz].filter(Boolean).join(', ')} {person.geb_ort_land ? `(${countryLabel(person.geb_ort_land)})` : ''}</td></tr>
            <tr><td className='text-muted'>Vorname Mutter</td><td>{person.vorname_m}</td></tr>
            <tr><td className='text-muted'>Vorname Vater</td><td>{person.vorname_v}</td></tr>
            <tr><td className='text-muted'>Staatsangehörigkeit</td><td>{countryLabel(person.staat_1)}{person.staat_2 ? `, ${countryLabel(person.staat_2)}` : ''}</td></tr>
            <tr><td className='text-muted'>Telefon</td><td>{person.kontakt_tel}</td></tr>
            <tr><td className='text-muted'>E-Mail</td><td>{person.kontakt_mail}</td></tr>
          </tbody>
        </Table>
      </section>

      <section className='form-section mb-4'>
        <h5 className='mb-3'>Wohnsitze</h5>
        {adressen.length === 0 ? (
          <p className='text-muted'>Keine Wohnsitze hinterlegt.</p>
        ) : (
          <Table striped bordered hover size='sm' responsive>
            <thead>
              <tr>
                <th>Land</th><th>PLZ</th><th>Ort</th><th>Straße</th><th>von</th><th>bis</th>
              </tr>
            </thead>
            <tbody>
              {adressen.map((a) => (
                <tr key={a.a_id}>
                  <td>{countryLabel(a.land)}</td>
                  <td>{a.plz}</td>
                  <td>{a.ort}</td>
                  <td>{[a.strasse, a.zusatz].filter(Boolean).join(' ')}</td>
                  <td>{formatDate(a.von)}</td>
                  <td>{a.bis ? formatDate(a.bis) : <Badge bg='success'>aktuell</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </section>

      <section className='form-section mb-4'>
        <h5 className='mb-3'>Beschäftigungen</h5>
        {beschaeftigung.length === 0 ? (
          <p className='text-muted'>Keine Beschäftigungen hinterlegt.</p>
        ) : (
          <Table striped bordered hover size='sm' responsive>
            <thead>
              <tr>
                <th>Art</th><th>Organisation</th><th>Tätigkeit</th><th>von</th><th>bis</th>
              </tr>
            </thead>
            <tbody>
              {beschaeftigung.map((b) => (
                <tr key={b.b_id}>
                  <td>{BESCHAEFTIGUNG_ART_LABEL[b.art] ?? b.art}</td>
                  <td>{b.name}</td>
                  <td>{b.info}</td>
                  <td>{formatDate(b.von)}</td>
                  <td>{b.bis ? formatDate(b.bis) : <Badge bg='success'>aktuell</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </section>

      <section className='form-section mb-4'>
        <h5 className='mb-3'>Dokumente</h5>
        <p className='text-muted small'>
          Aus Sicherheitsgründen werden nur die Eckdaten deiner Dokumente angezeigt — die
          Scans selbst sind hier nicht abrufbar.
        </p>
        {dokinfo.length === 0 ? (
          <p className='text-muted'>Keine Dokumente hinterlegt.</p>
        ) : (
          <Table striped bordered hover size='sm' responsive>
            <thead>
              <tr>
                <th>Art</th><th>Nummer</th><th>Ausstellung</th><th>Ablauf (geschätzt)</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {dokinfo.map((doc) => {
                const expiry = estimatedExpiry(doc);
                const monthsLeft = expiry ? expiry.diff(moment(), 'months') : null;
                return (
                  <tr key={doc.d_id}>
                    <td>{DOK_ART_LABEL[doc.art] || doc.dokname || `Dokument ${doc.d_id}`}</td>
                    <td>{maskNummer(doc.nummer)}</td>
                    <td>{formatDate(doc.ausstellung)}</td>
                    <td>{expiry ? `~ ${expiry.format(DATE_FORMAT)}` : '–'}</td>
                    <td>
                      {monthsLeft === null ? (
                        '–'
                      ) : doc.art === 0 ? (
                        // Strafregister (Bartek 31.08.2026): frischer Auszug nur bei
                        // AKTUELLEM Auslands-Wohnsitz nötig; für frühere Wohnsitze
                        // zählt die einmalige Vorlage — die ist mit diesem Eintrag erfüllt.
                        monthsLeft < 0 ? (
                          auslandsWohnsitz
                            ? <Badge bg='secondary'>älter als 6 Monate — vor der nächsten Verlängerung neu besorgen (aktueller Auslands-Wohnsitz)</Badge>
                            : <Badge bg='success'>einmalige Vorlage erfüllt — neu nur bei aktuellem Auslands-Wohnsitz nötig</Badge>
                        ) : (
                          <Badge bg='success'>verwendbar (max. 6 Monate ab Ausstellung)</Badge>
                        )
                      ) : monthsLeft < 0 ? (
                        <Badge bg='danger'>vermutlich abgelaufen — bitte neues Dokument hochladen</Badge>
                      ) : monthsLeft <= 6 ? (
                        <Badge bg='warning' text='dark'>läuft bald ab — bitte neues Dokument hochladen</Badge>
                      ) : (
                        <Badge bg='success'>gültig</Badge>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}

        <p className='text-muted mt-2 mb-0' style={{ fontSize: '0.85rem' }}>
          Ein neues Dokument (aktueller Ausweis-Scan, Strafregisterbescheinigung) kannst du
          <strong> jederzeit</strong> über die Kachel <strong>Dokument nachreichen</strong> auf der
          Startseite hinterlegen — unabhängig von Antrags-Zeitfenstern. Im Zuge eines Antrags
          (ZÜP-Verlängerung, Komplett-Antrag, Ausweis-Verlängerung) geht es natürlich auch direkt dort.
        </p>
      </section>
    </Container>
  );
}

export default MeinZuep;
